// A3 Administration (G01 à G03, charte D-038) : organisations et relations, comptes et affectations, rôles
// et droits. Les paramètres (G04) et le journal d'audit (G05) sont dans leurs propres fichiers.
// Chaque liste met en tête ce qui demande une action ; une ligne choisie ouvre son résumé à côté.

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTIONS,
  CODES_PROVINCES,
  DEFINITIONS_ROLES,
  LIBELLES_RELATIONS,
  NIVEAUX_CONFIDENTIALITE,
  RESSOURCES,
  ROLES,
  TYPES_ORGANISATIONS,
  TYPES_RELATIONS_ORGANISATIONS,
  type Ressource,
  type Role,
} from '@carburanttrack/shared';
import { LIBELLES_RESSOURCES_LOT3 } from '@carburanttrack/shared/logistique';
import { api } from '../../api/client';
import type { Organisation } from '../../api/types';
import { aUnRole, useSession } from '../../app/session';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { montrerSurPetitEcran } from '../../composants/selection';
import { dateHeure, libelleConfidentialite, libelleProvince, libelleRole, libelleTypeOrganisation } from '../../composants/format';
import { CadreAdministration } from './Cadre';

export { AdministrationParametres } from './Parametres';
export { JournalAudit } from './Journal';

function useOrganisations() {
  return useQuery({ queryKey: ['organisations', 'administration'], queryFn: async () => (await api<Organisation[]>('/api/v1/organisations?limite=200')).donnees });
}

const normaliser = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Organisations (G01) ---------------------------------------------------------------------------

export function AdministrationOrganisations() {
  const orgs = useOrganisations();
  const [recherche, setRecherche] = useState('');
  const [type, setType] = useState('');
  const [choisie, setChoisie] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);
  const liste = orgs.data ?? [];
  const visibles = liste.filter((o) => (!type || o.type === type) && (!recherche.trim() || normaliser(`${o.nom} ${o.sigle ?? ''}`).includes(normaliser(recherche.trim()))));
  const selection = liste.find((o) => o.id === choisie) ?? visibles[0] ?? null;
  const typesPresents = TYPES_ORGANISATIONS.filter((t) => liste.some((o) => o.type === t));
  const fictives = liste.filter((o) => o.estFictive).length;

  return (
    <CadreAdministration
      titre="Organisations"
      description={orgs.data ? `${liste.length} organisations actives, dont ${fictives} fictives du jeu de présentation.` : undefined}
      action={
        !creation && (
          <button type="button" className="bouton primaire" onClick={() => setCreation(true)}>
            <Icone nom="ajout" />
            Nouvelle organisation
          </button>
        )
      }
    >
      {orgs.isLoading && <Squelette lignes={6} />}
      {orgs.error && <MessageErreur erreur={orgs.error} />}
      {orgs.data && (
        <>
          <div className="barre-filtres">
            <div className="champ large">
              <label htmlFor="org-recherche">Rechercher une organisation</label>
              <input id="org-recherche" type="search" placeholder="Nom ou sigle" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
            </div>
            <div className="puces filtres-puces" role="radiogroup" aria-label="Filtrer par type">
              <button type="button" role="radio" className="puce" aria-checked={type === ''} onClick={() => setType('')}>
                Toutes <span className="compte">{liste.length}</span>
              </button>
              {typesPresents.map((t) => (
                <button key={t} type="button" role="radio" className="puce" aria-checked={type === t} onClick={() => setType(t)}>
                  {libelleTypeOrganisation(t)} <span className="compte">{liste.filter((o) => o.type === t).length}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="deux-colonnes">
            <section className="principal" aria-label="Liste des organisations">
              <div className="tableau-conteneur">
                <table className="tableau lignes-48">
                  <caption className="visuellement-cache">Organisations</caption>
                  <thead>
                    <tr>
                      <th scope="col">Nom</th>
                      <th scope="col">Type</th>
                      <th scope="col" className="masquable">
                        Province du siège
                      </th>
                      <th scope="col" className="masquable">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.length === 0 && (
                      <tr>
                        <td colSpan={4} className="attenue">
                          Aucune organisation ne correspond à cette recherche.
                        </td>
                      </tr>
                    )}
                    {visibles.map((o) => (
                      <tr key={o.id} aria-selected={selection?.id === o.id}>
                        <td>
                          <button type="button" className="bouton-ligne" aria-pressed={selection?.id === o.id} onClick={() => { setChoisie(o.id); montrerSurPetitEcran('titre-fiche-organisation'); }}>
                            <span>
                              {o.nom} {o.sigle && <span className="attenue">({o.sigle})</span>} {o.estFictive && <span className="etiquette gris">fictive</span>}
                            </span>
                          </button>
                        </td>
                        <td className="attenue">{libelleTypeOrganisation(o.type)}</td>
                        <td className="masquable">{o.provinceCode ? libelleProvince(o.provinceCode) : <span className="indisponible">non renseignée</span>}</td>
                        <td className="masquable">{o.statut === 'actif' ? 'Active' : 'Archivée'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <aside className="lateral">
              {creation && <FormulaireOrganisation fermer={() => setCreation(false)} />}
              {selection && <FicheOrganisationAdministration organisation={selection} organisations={liste} />}
            </aside>
          </div>
        </>
      )}
    </CadreAdministration>
  );
}

function FormulaireOrganisation({ fermer }: { fermer: () => void }) {
  const client = useQueryClient();
  const [nom, setNom] = useState('');
  const [sigle, setSigle] = useState('');
  const [type, setType] = useState('distributeur');
  const [province, setProvince] = useState('GA-1');
  const [fictive, setFictive] = useState(false);
  const creation = useMutation({
    mutationFn: () => api('/api/v1/organisations', { corps: { nom, sigle: sigle || null, type, provinceCode: province || null, estFictive: fictive } }),
    onSuccess: async () => {
      setNom('');
      setSigle('');
      await client.invalidateQueries({ queryKey: ['organisations'] });
    },
  });
  return (
    <form
      className="carte-bloc carte-formulaire"
      aria-labelledby="titre-nouvelle-organisation"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        creation.mutate();
      }}
    >
      <div className="carte-bloc-tete">
        <h2 id="titre-nouvelle-organisation">Nouvelle organisation</h2>
        <button type="button" className="bouton discret" onClick={fermer}>
          Fermer
        </button>
      </div>
      <div className="champ">
        <label htmlFor="org-nom">Nom</label>
        <input id="org-nom" value={nom} onChange={(e) => setNom(e.target.value)} />
      </div>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="org-sigle">Sigle</label>
          <input id="org-sigle" value={sigle} onChange={(e) => setSigle(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor="org-type">Type</label>
          <select id="org-type" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES_ORGANISATIONS.map((t) => (
              <option key={t} value={t}>
                {libelleTypeOrganisation(t)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="champ">
        <label htmlFor="org-province">Province du siège</label>
        <select id="org-province" value={province} onChange={(e) => setProvince(e.target.value)}>
          <option value="">Aucune</option>
          {CODES_PROVINCES.map((c) => (
            <option key={c} value={c}>
              {libelleProvince(c)}
            </option>
          ))}
        </select>
      </div>
      <label className="case">
        <input type="checkbox" checked={fictive} onChange={(e) => setFictive(e.target.checked)} />
        <span>Organisation fictive (jeu de présentation)</span>
      </label>
      {creation.error && <MessageErreur erreur={creation.error} titre="Création refusée" />}
      {creation.isSuccess && <Message type="succes">Organisation créée.</Message>}
      <button type="submit" className="bouton primaire pleine-largeur" disabled={!nom.trim() || creation.isPending}>
        Créer l’organisation
      </button>
    </form>
  );
}

function FicheOrganisationAdministration({ organisation: o, organisations }: { organisation: Organisation; organisations: Organisation[] }) {
  const client = useQueryClient();
  const [typeRelation, setTypeRelation] = useState('contrat_transport');
  const [partenaire, setPartenaire] = useState('');
  const [motifArchivage, setMotifArchivage] = useState('');
  const relation = useMutation({
    mutationFn: () => api('/api/v1/relations-organisations', { corps: { organisationSourceId: o.id, organisationCibleId: partenaire, type: typeRelation } }),
    onSuccess: async () => {
      setPartenaire('');
      await client.invalidateQueries({ queryKey: ['organisation'] });
    },
  });
  const archivage = useMutation({
    mutationFn: () => api(`/api/v1/organisations/${o.id}/archivage`, { corps: { motif: motifArchivage } }),
    onSuccess: async () => {
      setMotifArchivage('');
      await client.invalidateQueries({ queryKey: ['organisations'] });
    },
  });
  return (
    <section className="carte-bloc resume-installation" aria-labelledby="titre-fiche-organisation">
      <span className="sur-titre">{libelleTypeOrganisation(o.type)}</span>
      <h2 id="titre-fiche-organisation">{o.nom}</h2>
      {o.sigle && <span className="attenue">{o.sigle}</span>}
      <dl className="paires">
        <div>
          <dt>Province du siège</dt>
          <dd>{o.provinceCode ? libelleProvince(o.provinceCode) : <span className="indisponible">non renseignée</span>}</dd>
        </div>
        <div>
          <dt>Statut</dt>
          <dd>{o.statut === 'actif' ? 'Active' : 'Archivée'}</dd>
        </div>
        <div>
          <dt>Jeu de données</dt>
          <dd>{o.estFictive ? 'Fictive, jeu de présentation' : 'Institution ou organisation réelle'}</dd>
        </div>
      </dl>
      <Link className="bouton" to={`/referentiel/organisations/${o.id}`}>
        Ouvrir la fiche, relations et documents
      </Link>

      <form
        className="encart"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          relation.mutate();
        }}
      >
        <h3>Ajouter une relation</h3>
        <div className="champ">
          <label htmlFor="rel-type">Nature</label>
          <select id="rel-type" value={typeRelation} onChange={(e) => setTypeRelation(e.target.value)}>
            {TYPES_RELATIONS_ORGANISATIONS.map((t) => (
              <option key={t} value={t}>
                {LIBELLES_RELATIONS[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="rel-cible">Organisation partenaire</label>
          <select id="rel-cible" value={partenaire} onChange={(e) => setPartenaire(e.target.value)}>
            <option value="">Choisir</option>
            {organisations
              .filter((x) => x.id !== o.id)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.nom}
                </option>
              ))}
          </select>
        </div>
        <p className="attenue">Règle fixe : une relation entre deux distributeurs ne partage jamais de données.</p>
        {relation.error && <MessageErreur erreur={relation.error} titre="Relation refusée" />}
        {relation.isSuccess && <Message type="succes">Relation enregistrée.</Message>}
        <button type="submit" className="bouton primaire pleine-largeur" disabled={!partenaire || relation.isPending}>
          Enregistrer la relation
        </button>
      </form>

      <details className="zone-sensible">
        <summary>Archiver cette organisation</summary>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            archivage.mutate();
          }}
        >
          <div className="champ">
            <label htmlFor="arch-motif">Motif (obligatoire)</label>
            <input id="arch-motif" value={motifArchivage} onChange={(e) => setMotifArchivage(e.target.value)} />
          </div>
          <p className="attenue">Les affectations rattachées ne donnent plus accès. L’action est tracée dans le journal d’audit.</p>
          {archivage.error && <MessageErreur erreur={archivage.error} />}
          {archivage.isSuccess && <Message type="succes">Organisation archivée ; ses affectations ne donnent plus accès.</Message>}
          <button type="submit" className="bouton danger pleine-largeur" disabled={motifArchivage.trim().length < 3 || archivage.isPending}>
            Archiver
          </button>
        </form>
      </details>
    </section>
  );
}

// Comptes (G02) -----------------------------------------------------------------------------------

interface UtilisateurListe {
  id: string;
  courriel: string;
  prenom: string;
  nom: string;
  statut: string;
  mfaActif: boolean;
  derniereConnexionLe: string | null;
  affectations: { role: string; organisationNom: string }[];
}

type FiltreComptes = 'tous' | 'sensibles' | 'jamais' | 'suspendus';

const roleSensible = (u: UtilisateurListe) => u.affectations.some((a) => DEFINITIONS_ROLES[a.role as Role]?.mfaObligatoire);
const sensibleSansMfa = (u: UtilisateurListe) => roleSensible(u) && !u.mfaActif;
const initiales = (u: { prenom: string; nom: string }) => `${u.prenom.charAt(0)}${u.nom.charAt(0)}`.toUpperCase();

export function AdministrationUtilisateurs() {
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState<FiltreComptes>('tous');
  const [choisi, setChoisi] = useState<string | null>(null);
  const liste = useQuery({
    queryKey: ['utilisateurs'],
    queryFn: async () => api<UtilisateurListe[]>('/api/v1/utilisateurs?limite=200'),
  });
  const comptes = liste.data?.donnees ?? [];
  const correspond = (u: UtilisateurListe, f: FiltreComptes) =>
    f === 'tous' ? true : f === 'sensibles' ? sensibleSansMfa(u) : f === 'jamais' ? !u.derniereConnexionLe : u.statut === 'suspendu';
  const q = normaliser(recherche.trim());
  const visibles = comptes.filter((u) => correspond(u, filtre) && (!q || normaliser(`${u.prenom} ${u.nom} ${u.courriel} ${u.affectations.map((a) => `${libelleRole(a.role)} ${a.organisationNom}`).join(' ')}`).includes(q)));
  const selection = comptes.find((u) => u.id === choisi) ?? visibles[0] ?? null;
  const sensibles = comptes.filter(sensibleSansMfa).length;
  const jamais = comptes.filter((u) => !u.derniereConnexionLe).length;
  const FILTRES: { cle: FiltreComptes; libelle: string }[] = [
    { cle: 'tous', libelle: 'Tous' },
    { cle: 'sensibles', libelle: 'Rôle sensible sans second facteur' },
    { cle: 'jamais', libelle: 'Jamais connectés' },
    { cle: 'suspendus', libelle: 'Suspendus' },
  ];

  return (
    <CadreAdministration
      titre="Comptes utilisateurs"
      description={liste.data ? `${comptes.length} comptes visibles${liste.data.meta.curseurSuivant ? ' (200 premiers affichés)' : ''}.` : undefined}
      action={
        <Link className="bouton primaire" to="/administration/utilisateurs/nouveau">
          <Icone nom="ajout" />
          Nouveau compte
        </Link>
      }
    >
      {liste.isLoading && <Squelette lignes={8} />}
      {liste.error && <MessageErreur erreur={liste.error} />}
      {liste.data && (
        <>
          {sensibles > 0 && (
            <section className="a-faire rouge" aria-labelledby="titre-exception-mfa">
              <div>
                <span className="sur-titre">À traiter en priorité</span>
                <h2 id="titre-exception-mfa">
                  {sensibles} compte{sensibles > 1 ? 's' : ''} à rôle sensible sans second facteur
                </h2>
                <p>
                  Leur rôle impose le second facteur : l’enrôlement leur est demandé à la prochaine connexion, sauf par les comptes de présentation. {jamais} compte{jamais > 1 ? 's ne se sont' : ' ne s’est'} jamais connecté{jamais > 1 ? 's' : ''}.
                </p>
              </div>
              <button type="button" className="bouton" onClick={() => setFiltre('sensibles')}>
                Voir ces comptes
              </button>
            </section>
          )}
          <div className="barre-filtres">
            <div className="champ large">
              <label htmlFor="u-recherche">Rechercher un compte</label>
              <input id="u-recherche" type="search" placeholder="Nom, courriel, rôle ou organisation" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
            </div>
            <div className="puces filtres-puces" role="radiogroup" aria-label="Filtrer les comptes">
              {FILTRES.map((f) => (
                <button key={f.cle} type="button" role="radio" className="puce" aria-checked={filtre === f.cle} onClick={() => setFiltre(f.cle)}>
                  {f.libelle} <span className="compte">{comptes.filter((u) => correspond(u, f.cle)).length}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="deux-colonnes">
            <section className="principal" aria-label="Liste des comptes">
              <div className="tableau-conteneur">
                <table className="tableau lignes-48">
                  <caption className="visuellement-cache">Comptes utilisateurs</caption>
                  <thead>
                    <tr>
                      <th scope="col">Compte</th>
                      <th scope="col">Rôle et organisation</th>
                      <th scope="col" className="masquable">
                        Second facteur
                      </th>
                      <th scope="col" className="masquable">
                        Dernière connexion
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.length === 0 && (
                      <tr>
                        <td colSpan={4} className="attenue">
                          Aucun compte ne correspond.
                        </td>
                      </tr>
                    )}
                    {visibles.map((u) => (
                      <tr key={u.id} aria-selected={selection?.id === u.id}>
                        <td>
                          <button type="button" className="bouton-ligne" aria-pressed={selection?.id === u.id} onClick={() => { setChoisi(u.id); montrerSurPetitEcran('titre-compte-choisi'); }}>
                            {u.prenom} {u.nom}
                            <small>{u.courriel}</small>
                          </button>
                        </td>
                        <td>
                          {u.affectations[0] ? libelleRole(u.affectations[0].role) : <span className="attenue">aucune affectation active</span>}
                          {u.affectations[0] && (
                            <small>
                              {u.affectations[0].organisationNom}
                              {u.affectations.length > 1 ? `, et ${u.affectations.length - 1} autre${u.affectations.length > 2 ? 's' : ''}` : ''}
                            </small>
                          )}
                          {u.statut !== 'actif' && <span className="etiquette rouge">{u.statut}</span>}
                        </td>
                        <td className="masquable">
                          <EtatMfa u={u} />
                        </td>
                        <td className="masquable">{u.derniereConnexionLe ? dateHeure(u.derniereConnexionLe) : <span className="attenue">jamais</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            {selection && (
              <aside className="lateral collant" aria-labelledby="titre-compte-choisi">
                <section className="carte-bloc resume-installation">
                  <div className="identite-compte">
                    <span className="initiales" aria-hidden="true">
                      {initiales(selection)}
                    </span>
                    <span>
                      <h2 id="titre-compte-choisi">
                        {selection.prenom} {selection.nom}
                      </h2>
                      <small>{selection.courriel}</small>
                    </span>
                  </div>
                  <dl className="paires">
                    {selection.affectations.map((a, i) => (
                      <div key={i}>
                        <dt>{libelleRole(a.role)}</dt>
                        <dd>{a.organisationNom}</dd>
                      </div>
                    ))}
                    <div>
                      <dt>Statut</dt>
                      <dd>{selection.statut === 'actif' ? 'Actif' : selection.statut}</dd>
                    </div>
                    <div>
                      <dt>Second facteur</dt>
                      <dd>
                        <EtatMfa u={selection} />
                      </dd>
                    </div>
                    <div>
                      <dt>Dernière connexion</dt>
                      <dd>{selection.derniereConnexionLe ? dateHeure(selection.derniereConnexionLe) : 'jamais'}</dd>
                    </div>
                  </dl>
                  {sensibleSansMfa(selection) && <p className="message erreur">Rôle sensible sans second facteur : l’enrôlement est exigé à sa prochaine connexion.</p>}
                  <Link className="bouton primaire" to={`/administration/utilisateurs/${selection.id}`}>
                    Ouvrir la fiche du compte
                  </Link>
                  <p className="attenue">Suspension, réactivation, réinitialisation du mot de passe ou du second facteur et affectations se font depuis la fiche, avec un motif inscrit au journal d’audit.</p>
                </section>
              </aside>
            )}
          </div>
        </>
      )}
    </CadreAdministration>
  );
}

function EtatMfa({ u }: { u: UtilisateurListe }) {
  if (u.mfaActif) return <span className="point-etat vert">Actif</span>;
  if (roleSensible(u)) return <span className="point-etat rouge">Non enrôlé, requis</span>;
  return <span className="point-etat">Non enrôlé</span>;
}

function ChampsAffectation({ valeur, changer, prefixe }: { valeur: AffectationSaisie; changer: (v: AffectationSaisie) => void; prefixe: string }) {
  const orgs = useOrganisations();
  const def = DEFINITIONS_ROLES[valeur.role as Role];
  return (
    <>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor={`${prefixe}-org`}>Organisation</label>
          <select id={`${prefixe}-org`} value={valeur.organisationId} onChange={(e) => changer({ ...valeur, organisationId: e.target.value })}>
            <option value="">Choisir</option>
            {orgs.data?.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor={`${prefixe}-role`}>Rôle</label>
          <select id={`${prefixe}-role`} value={valeur.role} onChange={(e) => changer({ ...valeur, role: e.target.value })}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {DEFINITIONS_ROLES[r].libelle}
              </option>
            ))}
          </select>
          <span className="aide">{def?.description}</span>
        </div>
        <div className="champ">
          <label htmlFor={`${prefixe}-fin`}>Date de fin {def?.dateFinObligatoire ? '(obligatoire)' : '(facultative)'}</label>
          <input id={`${prefixe}-fin`} type="date" value={valeur.dateFin} onChange={(e) => changer({ ...valeur, dateFin: e.target.value })} />
        </div>
      </div>
      <fieldset className="champ cadre-provinces">
        <legend>Périmètre territorial (aucune case : national)</legend>
        <div className="puces">
          {CODES_PROVINCES.map((c) => (
            <label key={c} className="case case-compacte">
              <input
                type="checkbox"
                checked={valeur.provinces.includes(c)}
                onChange={(e) => changer({ ...valeur, provinces: e.target.checked ? [...valeur.provinces, c] : valeur.provinces.filter((p) => p !== c) })}
              />
              <span>{libelleProvince(c)}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </>
  );
}

interface AffectationSaisie {
  organisationId: string;
  role: string;
  provinces: string[];
  dateFin: string;
}

const AFFECTATION_VIDE: AffectationSaisie = { organisationId: '', role: 'distributeur', provinces: [], dateFin: '' };

function versAffectation(a: AffectationSaisie) {
  return {
    organisationId: a.organisationId,
    role: a.role,
    perimetre: { provinces: a.provinces, departements: [], installations: [] },
    ...(a.dateFin ? { dateFin: new Date(`${a.dateFin}T23:59:59Z`).toISOString() } : {}),
  };
}

export function NouvelUtilisateur() {
  const client = useQueryClient();
  const [courriel, setCourriel] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [fonction, setFonction] = useState('');
  const [affectation, setAffectation] = useState<AffectationSaisie>(AFFECTATION_VIDE);
  const creation = useMutation({
    mutationFn: async () =>
      (await api<{ id: string; motDePasseInitial: string }>('/api/v1/utilisateurs', { corps: { courriel, prenom, nom, fonction: fonction || null, affectations: [versAffectation(affectation)] } })).donnees,
    onSuccess: () => client.invalidateQueries({ queryKey: ['utilisateurs'] }),
  });
  if (creation.data)
    return (
      <CadreAdministration titre="Compte créé" contexte="Comptes utilisateurs">
        <section className="a-faire" aria-labelledby="titre-mot-de-passe-initial">
          <div>
            <span className="sur-titre">À transmettre hors de la plateforme</span>
            <h2 id="titre-mot-de-passe-initial">Mot de passe initial de {courriel}</h2>
            <p>
              Affiché une seule fois : <code className="secret">{creation.data.motDePasseInitial}</code>. Son changement sera imposé à la première connexion.
            </p>
          </div>
          <Link className="bouton primaire" to={`/administration/utilisateurs/${creation.data.id}`}>
            Ouvrir la fiche du compte
          </Link>
        </section>
      </CadreAdministration>
    );
  return (
    <CadreAdministration titre="Nouveau compte" contexte="Comptes utilisateurs" description="Un mot de passe initial est généré ; son changement est imposé à la première connexion.">
      <form
        className="carte-bloc"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          creation.mutate();
        }}
      >
        <h2>Identité</h2>
        <div className="formulaire-ligne">
          <div className="champ">
            <label htmlFor="n-courriel">Courriel</label>
            <input id="n-courriel" type="email" value={courriel} onChange={(e) => setCourriel(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor="n-prenom">Prénom</label>
            <input id="n-prenom" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor="n-nom-u">Nom</label>
            <input id="n-nom-u" value={nom} onChange={(e) => setNom(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor="n-fonction">Fonction</label>
            <input id="n-fonction" value={fonction} onChange={(e) => setFonction(e.target.value)} />
          </div>
        </div>
        <h2>Première affectation</h2>
        <ChampsAffectation valeur={affectation} changer={setAffectation} prefixe="n-aff" />
        {creation.error && <MessageErreur erreur={creation.error} titre="Création refusée" />}
        <div className="actions">
          <button type="submit" className="bouton primaire" disabled={!courriel || !prenom || !nom || !affectation.organisationId || creation.isPending}>
            Créer le compte
          </button>
          <Link className="bouton" to="/administration/utilisateurs">
            Annuler
          </Link>
        </div>
      </form>
    </CadreAdministration>
  );
}

interface UtilisateurDetaille extends UtilisateurListe {
  fonction: string | null;
  telephone: string | null;
  verrouilleJusquA: string | null;
  motifSuspension: string | null;
  sessionsActives: number;
  doitChangerMotDePasse: boolean;
  affectations: { id: string; role: string; organisationNom: string; perimetre: { provinces: string[]; installations: string[] }; dateDebut: string; dateFin: string | null; statut: string; motif: string | null }[];
}

export function FicheUtilisateur() {
  const { id } = useParams();
  const client = useQueryClient();
  const { etat } = useSession();
  const naviguer = useNavigate();
  const r = useQuery({ queryKey: ['utilisateur', id], queryFn: async () => (await api<UtilisateurDetaille>(`/api/v1/utilisateurs/${id}`)).donnees });
  const [motif, setMotif] = useState('');
  const [resultat, setResultat] = useState<string | null>(null);
  const [nouvelle, setNouvelle] = useState<AffectationSaisie>(AFFECTATION_VIDE);
  const action = useMutation({
    mutationFn: async ({ chemin, corps }: { chemin: string; corps: unknown }) => (await api<{ sessionsRevoquees?: number; motDePasseInitial?: string }>(chemin, { corps })).donnees,
    onSuccess: async (d, v) => {
      setMotif('');
      setResultat(
        d.motDePasseInitial
          ? `Nouveau mot de passe initial, à transmettre hors de la plateforme : ${d.motDePasseInitial}`
          : `Opération enregistrée${d.sessionsRevoquees !== undefined ? ` ; sessions révoquées : ${d.sessionsRevoquees}` : ''}.`,
      );
      if (v.chemin.endsWith('/affectations')) setNouvelle(AFFECTATION_VIDE);
      await client.invalidateQueries({ queryKey: ['utilisateur', id] });
      await client.invalidateQueries({ queryKey: ['utilisateurs'] });
    },
  });
  if (r.isLoading) return <Squelette lignes={8} />;
  if (r.error) return <MessageErreur erreur={r.error} />;
  const u = r.data!;
  const lui = etat?.utilisateur?.id === u.id;
  const motifValide = motif.trim().length >= 3;
  const operation = (suffixe: string) => action.mutate({ chemin: `/api/v1/utilisateurs/${u.id}/${suffixe}`, corps: { motif } });
  const sensible = u.affectations.some((a) => a.statut === 'actif' && DEFINITIONS_ROLES[a.role as Role]?.mfaObligatoire);
  return (
    <CadreAdministration titre={`${u.prenom} ${u.nom}`} contexte="Comptes utilisateurs" description={u.courriel}>
      {resultat && <Message type="succes">{resultat}</Message>}
      {action.error && <MessageErreur erreur={action.error} titre="Opération refusée" />}
      {sensible && !u.mfaActif && <p className="message erreur">Rôle sensible sans second facteur : l’enrôlement est exigé à la prochaine connexion.</p>}
      <div className="grille-2">
        <section className="carte-bloc" aria-labelledby="titre-compte">
          <h2 id="titre-compte">Compte</h2>
          <dl className="paires">
            <div>
              <dt>Fonction</dt>
              <dd>{u.fonction ?? <span className="indisponible">non renseignée</span>}</dd>
            </div>
            <div>
              <dt>Statut</dt>
              <dd>
                {u.statut === 'actif' ? 'Actif' : u.statut}
                {u.motifSuspension && <small>{u.motifSuspension}</small>}
              </dd>
            </div>
            <div>
              <dt>Second facteur</dt>
              <dd>{u.mfaActif ? <span className="point-etat vert">Actif</span> : <span className={`point-etat${sensible ? ' rouge' : ''}`}>Non enrôlé</span>}</dd>
            </div>
            <div>
              <dt>Verrouillage</dt>
              <dd>{u.verrouilleJusquA && new Date(u.verrouilleJusquA) > new Date() ? `jusqu’à ${dateHeure(u.verrouilleJusquA)}` : 'aucun'}</dd>
            </div>
            <div>
              <dt>Sessions actives</dt>
              <dd>{u.sessionsActives}</dd>
            </div>
            <div>
              <dt>Dernière connexion</dt>
              <dd>{u.derniereConnexionLe ? dateHeure(u.derniereConnexionLe) : 'jamais'}</dd>
            </div>
          </dl>
        </section>
        <section className="carte-bloc" aria-labelledby="titre-actions-compte">
          <h2 id="titre-actions-compte">Actions sur le compte</h2>
          <div className="champ">
            <label htmlFor="action-motif">Motif (obligatoire, inscrit au journal d’audit)</label>
            <input id="action-motif" value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
          <div className="actions colonne">
            {u.statut === 'actif' && !lui && (
              <button type="button" className="bouton danger" disabled={!motifValide} onClick={() => operation('suspension')}>
                Suspendre le compte
              </button>
            )}
            {u.statut === 'suspendu' && (
              <button type="button" className="bouton primaire" disabled={!motifValide} onClick={() => operation('reactivation')}>
                Réactiver le compte
              </button>
            )}
            <button type="button" className="bouton" disabled={!motifValide} onClick={() => operation('reinitialisation-mot-de-passe')}>
              Réinitialiser le mot de passe
            </button>
            <button type="button" className="bouton" disabled={!motifValide} onClick={() => operation('reinitialisation-mfa')}>
              Réinitialiser le second facteur
            </button>
            <button type="button" className="bouton" disabled={!motifValide} onClick={() => operation('deverrouillage')}>
              Déverrouiller
            </button>
          </div>
        </section>
      </div>
      <section className="carte-bloc" aria-labelledby="titre-affectations">
        <h2 id="titre-affectations">Affectations</h2>
        <div className="tableau-conteneur">
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Rôle</th>
                <th scope="col">Organisation</th>
                <th scope="col" className="masquable">
                  Périmètre
                </th>
                <th scope="col" className="masquable">
                  Période
                </th>
                <th scope="col">État</th>
                <th scope="col">
                  <span className="visuellement-cache">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {u.affectations.map((a) => (
                <tr key={a.id}>
                  <td>{libelleRole(a.role)}</td>
                  <td>{a.organisationNom}</td>
                  <td className="masquable">
                    {a.perimetre.provinces.length ? a.perimetre.provinces.map(libelleProvince).join(', ') : a.perimetre.installations.length ? `${a.perimetre.installations.length} installation(s)` : 'national'}
                  </td>
                  <td className="masquable">
                    du {dateHeure(a.dateDebut)}
                    {a.dateFin && <small>au {dateHeure(a.dateFin)}</small>}
                  </td>
                  <td>
                    <span className={a.statut === 'actif' ? 'etiquette' : 'etiquette gris'}>{a.statut === 'actif' ? 'Active' : 'Terminée'}</span>
                  </td>
                  <td className="nombre">
                    {a.statut === 'actif' && (
                      <button
                        type="button"
                        className="bouton"
                        disabled={!motifValide}
                        title={motifValide ? undefined : 'Saisissez d’abord le motif dans « Actions sur le compte »'}
                        onClick={() => action.mutate({ chemin: `/api/v1/utilisateurs/${u.id}/affectations/${a.id}/fin`, corps: { motif } })}
                      >
                        Terminer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="attenue espace-haut">Terminer une affectation utilise le motif saisi dans « Actions sur le compte ».</p>
        <form
          className="encart"
          onSubmit={(e) => {
            e.preventDefault();
            action.mutate({ chemin: `/api/v1/utilisateurs/${u.id}/affectations`, corps: { ...versAffectation(nouvelle), ...(motif ? { motif } : {}) } });
          }}
        >
          <h3>Ajouter une affectation</h3>
          <ChampsAffectation valeur={nouvelle} changer={setNouvelle} prefixe="a-aff" />
          <button type="submit" className="bouton primaire" disabled={!nouvelle.organisationId}>
            Ajouter l’affectation
          </button>
        </form>
      </section>
      <button type="button" className="bouton" onClick={() => naviguer('/administration/utilisateurs')}>
        <Icone nom="retour" />
        Revenir à la liste des comptes
      </button>
    </CadreAdministration>
  );
}

// Rôles et droits (G03) ---------------------------------------------------------------------------

interface Permission {
  action: string;
  ressource: string;
  niveauMax: string;
}

interface RoleDetaille {
  code: Role;
  libelle: string;
  description: string;
  mfaObligatoire: boolean;
  permissions: Permission[];
}

const GROUPES_ROLES: { titre: string; roles: Role[] }[] = [
  { titre: 'Ministère et supervision', roles: ['ministre_cabinet', 'secretaire_general', 'directeur_general', 'directeur', 'chef_service', 'superviseur', 'autorite_provinciale', 'caistab'] },
  { titre: 'Administration', roles: ['administrateur_national', 'agent_administratif'] },
  { titre: 'Contrôle', roles: ['inspecteur', 'agent_terrain'] },
  { titre: 'Opérateurs', roles: ['depot', 'distributeur', 'transporteur', 'station', 'etablissement_prioritaire', 'externe_habilite'] },
  { titre: 'Technique et public', roles: ['support_technique', 'administrateur_idg', 'citoyen'] },
];

const LIBELLES_RESSOURCES: Record<Ressource, string> = {
  organisations: 'Organisations',
  utilisateurs: 'Comptes utilisateurs',
  habilitations: 'Habilitations',
  parametres: 'Paramètres',
  fonctionnalites: 'Fonctionnalités',
  installations: 'Installations',
  reservoirs: 'Réservoirs',
  vehicules: 'Véhicules',
  conducteurs: 'Conducteurs',
  localites: 'Localités',
  territoires: 'Territoires',
  catalogue: 'Catalogue',
  documents: 'Documents',
  audit: 'Journal d’audit',
  recherche: 'Recherche',
  synchronisation: 'Synchronisation',
  stocks: 'Stocks et déclarations',
  mouvements: 'Mouvements',
  positions: 'Positions par propriétaire',
  corrections: 'Corrections',
  anomalies: 'Anomalies',
  approvisionnements: 'Approvisionnements',
  gpl: 'GPL',
  autonomie: 'Autonomie',
  alertes: 'Alertes',
  regles_alerte: 'Règles d’alerte',
  notifications: 'Notifications',
  imports: 'Imports',
  echeances: 'Échéances déclaratives',
  agregats: 'Agrégats nationaux',
  inspections: 'Inspections',
  constats: 'Constats d’inspection',
  incidents: 'Incidents',
  prix: 'Prix réglementés',
  observations_prix: 'Observations de prix',
  integrations: 'Connecteurs et intégrations',
  ...LIBELLES_RESSOURCES_LOT3,
  journaux_techniques: 'Journaux techniques',
  etat_services: 'État des services',
  parametres_exploitation: 'Paramètres d’exploitation',
};

const LIBELLES_ACTIONS: Record<string, string> = { lire: 'Lire', creer: 'Créer', modifier: 'Modifier', valider: 'Valider', exporter: 'Exporter', administrer: 'Administrer' };

const cleDroit = (p: { action: string; ressource: string }) => `${p.action}:${p.ressource}`;

export function AdministrationRoles() {
  const { etat } = useSession();
  const client = useQueryClient();
  const roles = useQuery({ queryKey: ['roles'], queryFn: async () => (await api<RoleDetaille[]>('/api/v1/roles')).donnees });
  const [choisi, setChoisi] = useState<Role>('distributeur');
  const [brouillon, setBrouillon] = useState<Permission[] | null>(null);
  const [motif, setMotif] = useState('');
  const modifiable = aUnRole(etat, 'administrateur_national');
  const enregistrement = useMutation({
    mutationFn: () => api(`/api/v1/roles/${choisi}/permissions`, { methode: 'PUT', corps: { permissions: brouillon, motif } }),
    onSuccess: async () => {
      setBrouillon(null);
      setMotif('');
      await client.invalidateQueries({ queryKey: ['roles'] });
    },
  });
  const role = roles.data?.find((r) => r.code === choisi);
  const lignes = brouillon ?? role?.permissions ?? [];
  const parCle = new Map(lignes.map((p) => [cleDroit(p), p.niveauMax]));
  const origine = new Map((role?.permissions ?? []).map((p) => [cleDroit(p), p.niveauMax]));
  const changements = brouillon ? new Set([...parCle.keys(), ...origine.keys()].filter((k) => parCle.get(k) !== origine.get(k))).size : 0;
  const changer = (action: string, ressource: string, niveau: string) => {
    const autres = lignes.filter((p) => !(p.action === action && p.ressource === ressource));
    setBrouillon(niveau ? [...autres, { action, ressource, niveauMax: niveau }] : autres);
  };

  return (
    <CadreAdministration
      titre="Rôles et droits"
      description="Ce que chaque rôle peut lire, créer, modifier, valider, exporter ou administrer, et jusqu’à quel niveau de confidentialité."
      action={
        brouillon && changements > 0 ? (
          <form
            className="bandeau-brouillon"
            onSubmit={(e) => {
              e.preventDefault();
              enregistrement.mutate();
            }}
          >
            <strong>
              Brouillon : {changements} modification{changements > 1 ? 's' : ''}
            </strong>
            <div className="champ">
              <label htmlFor="perm-motif" className="visuellement-cache">
                Motif de la modification
              </label>
              <input id="perm-motif" placeholder="Motif (obligatoire)" value={motif} onChange={(e) => setMotif(e.target.value)} />
            </div>
            <button type="button" className="bouton" onClick={() => setBrouillon(null)}>
              Annuler
            </button>
            <button type="submit" className="bouton primaire" disabled={motif.trim().length < 3 || enregistrement.isPending}>
              Publier
            </button>
          </form>
        ) : undefined
      }
    >
      {roles.isLoading && <Squelette lignes={8} />}
      {roles.error && <MessageErreur erreur={roles.error} />}
      {enregistrement.error && <MessageErreur erreur={enregistrement.error} titre="Modification refusée" />}
      {enregistrement.isSuccess && <Message type="succes">Droits publiés et tracés dans le journal d’audit.</Message>}
      {roles.data && role && (
        <div className="deux-colonnes colonnes-inversees">
          <div className="champ choix-role-compact">
            <label htmlFor="role-choisi">Rôle</label>
            <select
              id="role-choisi"
              value={choisi}
              onChange={(e) => {
                setChoisi(e.target.value as Role);
                setBrouillon(null);
              }}
            >
              {GROUPES_ROLES.map((g) => (
                <optgroup key={g.titre} label={g.titre}>
                  {g.roles
                    .filter((code) => roles.data.some((r) => r.code === code))
                    .map((code) => (
                      <option key={code} value={code}>
                        {DEFINITIONS_ROLES[code].libelle}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>
          <nav className="lateral carte-bloc liste-roles" aria-label={`${roles.data.length} rôles`}>
            <h2 className="sur-titre">{roles.data.length} rôles</h2>
            {GROUPES_ROLES.map((g) => (
              <div key={g.titre} className="groupe-roles">
                <span className="sur-titre">{g.titre}</span>
                {g.roles
                  .filter((code) => roles.data.some((r) => r.code === code))
                  .map((code) => (
                    <button
                      key={code}
                      type="button"
                      aria-pressed={choisi === code}
                      onClick={() => {
                        setChoisi(code);
                        setBrouillon(null);
                      }}
                    >
                      {DEFINITIONS_ROLES[code].libelle}
                    </button>
                  ))}
              </div>
            ))}
          </nav>
          <section className="principal" aria-labelledby="titre-role">
            <div className="carte-bloc tete-role">
              <div>
                <h2 id="titre-role">{role.libelle}</h2>
                <p className="attenue">{role.description}</p>
              </div>
              <span className="etiquette">{role.mfaObligatoire ? 'Second facteur obligatoire' : 'Second facteur facultatif'}</span>
            </div>
            <div className="tableau-conteneur">
              <table className="tableau matrice-droits">
                <caption>{modifiable ? 'Choisissez un niveau dans une case pour changer le droit. Les changements restent en brouillon jusqu’à publication.' : 'Consultation seule : la modification est réservée à l’administrateur national.'}</caption>
                <thead>
                  <tr>
                    <th scope="col">Ressource</th>
                    {ACTIONS.map((a) => (
                      <th key={a} scope="col">
                        {LIBELLES_ACTIONS[a]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {RESSOURCES.map((res) => (
                    <tr key={res}>
                      <th scope="row">{LIBELLES_RESSOURCES[res] ?? res}</th>
                      {ACTIONS.map((a) => {
                        const niveau = parCle.get(`${a}:${res}`) ?? '';
                        const modifie = brouillon !== null && niveau !== (origine.get(`${a}:${res}`) ?? '');
                        return (
                          <td key={a} className={`${niveau ? 'accorde' : 'refuse'}${modifie ? ' modifie' : ''}`}>
                            {modifiable ? (
                              <select aria-label={`${LIBELLES_ACTIONS[a]}, ${LIBELLES_RESSOURCES[res] ?? res}`} value={niveau} onChange={(e) => changer(a, res, e.target.value)}>
                                <option value="">Aucun</option>
                                {NIVEAUX_CONFIDENTIALITE.map((n) => (
                                  <option key={n} value={n}>
                                    {libelleConfidentialite(n)}
                                  </option>
                                ))}
                              </select>
                            ) : niveau ? (
                              libelleConfidentialite(niveau)
                            ) : (
                              <span className="attenue">Aucun</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="attenue espace-haut">
              Le niveau indiqué est le niveau de confidentialité maximal accessible : il couvre les niveaux inférieurs, dans l’ordre croissant : Publique, Diffusion restreinte, Confidentielle, Stratégique, Personnelle. « Aucun » : action non autorisée. Les règles fixes du cadrage s’appliquent toujours.
            </p>
          </section>
        </div>
      )}
    </CadreAdministration>
  );
}
